<template>
    <div>
      <div v-if="isOverlayDisabled || !item.duration" class="disabled-overlay"></div>
      <div class="clip-timelines p10">
        <div class="timeline-controls flex items-center">
          <i class="fa link-pointer ml10 text-white"
            :class="{
                'fa-pause': item.isPlaying,
                'fa-play': !item.isPlaying
            }"
            aria-hidden="true" @click="handlePlayPauseVideo(item)">
          </i>
          <i class="fa fa-step-backward mx10 link-pointer" @click="rewindCurrentPlaybackTime(item)" aria-hidden="true"></i>
          <i class="fa fa-step-forward mr10 link-pointer" @click="fastForwardCurrentPlaybackTime(item)" aria-hidden="true"></i>
          <span class="text-xs mr4 d-flex justify-center items-center time-range-display">
            {{ getFormattedTimeRange(item) }}
          </span>
        </div>
        <div class="timeline-container" ref="timelineContainer"
          @mousedown="startPlaybackHandleDrag($event, item)" 
          @mousemove="handleTimelineKeyframeUpdate($event, item)" 
          @mouseenter="handleTimelineKeyframeUpdate($event, item)"
          @mouseleave="handleTimelineKeyframeUpdateLeave($event)"
        >
          <div class="timeline">
            <div class="timeline-line" ref="timelineLine">
              <div v-if="(item.isExtendedToOriginal || item.isMerged) && (!item.isClipped && !item.isDeleted)" class="original-clip-pos-line" ref="originalClipPosLine"></div>
            </div>
          </div>
          <div class="handle-wrapper" 
            :style="{ left: item.handleLeft + '%' }">
            <div id="handle"></div>
          </div>
          <div>
            <div  
            class="trim-start-handle"
            @mousedown="startTrimHandlesDrag('start', item)"
            :style="{ left: item.handles.start.position + '%' }"
            @dblclick="handleTrimHandleDoubleClick(item)"
            >
            </div>
            <div 
            class="trim-end-handle"
            @mousedown="startTrimHandlesDrag('end', item)"
            :style="{ left: item.handles.end.position + '%' }"
            >
            </div>
          </div>
          <div>
            <div v-if="(item.isExtendedToOriginal || item.isMerged) && (!item.isClipped && !item.isDeleted)" class="original-start-end-marker" :style="{ left: calculateOriginalClipPos.clipStartPercentage + '%' }"></div>
            <div v-if="(item.isExtendedToOriginal || item.isMerged) && (!item.isClipped && !item.isDeleted)" class="original-start-end-marker" :style="{ left: calculateOriginalClipPos.clipEndPercentage + '%' }"></div>
          </div>
          <div class="preview-img-container" ref="previewImgContainer">
            <div v-if="item.keyframes.length === 0" class="preview-img"></div>
            <img  v-if="item.mimeType === 'video/mp4' && currentKeyFrameBlob" ref="previewImg" class="preview-img" :src="currentKeyFrameBlob" style="height: 80px;" >
            <div v-else-if="item.mimeType === 'video/mp4'" class="preview-img" style="height: 80px;"></div>
            <span class="preview-img-time-tooltip" :class="{ 'p4': item.mimeType === 'audio/mp3'}" >
              <span v-if="isAirtimeMode">{{ calculateAirtimeWithVideoOffset() }}</span>
              <span v-else>{{ formatHandleTooltipTime(item.duration, this.progress) }}</span>
            </span>          
          </div>
        </div>
      </div>
      <div class="d-flex metadata-container items-center justify-between">
        <div class="metadata text-xs p4 d-flex items-center">
          <div class="flex items-center pl5">
            <button v-if="!item.isDeleted" 
              @click="openConfirmationModal('clip', item)" 
              :disabled="item.handles.start.time === 0 && item.handles.end.time === item.duration" 
              v-tooltip="$t('mediaClipper.tooltips.trim_tooltip_text')" 
              class="mr10 mb0 btn btn-xs btn-danger max-content-width inline-block btn-text-xs">
                {{$t('mediaClipper.clippingMetadata.clip')}}
            </button>
            <button  
              @click="openConfirmationModal('delete', item)" 
              :disabled="item.handles.start.time === 0 && item.handles.end.time === item.duration" 
              v-tooltip="$t('mediaClipper.tooltips.delete_tooltip_text')" 
              class="mr10 mb0 btn btn-xs btn-danger max-content-width inline-block btn-text-xs">
              {{$t('mediaClipper.clippingMetadata.delete')}}
            </button>
            <button v-if="item.originalAssetExtendedData && !item.isExtendedToOriginal && item.originalAssetExtendedData.filepath && !item.isDeleted && !item.isClipped"  
              @click="handleExtendAsset(item)" 
              class="mr10 mb0 btn btn-xs btn-carma max-content-width inline-block btn-text-xs">
              {{item.duration > item.originalAssetExtendedData.duration ? $t('mediaClipper.clippingMetadata.restore') : $t('mediaClipper.clippingMetadata.extend')}}
            </button>
            <div v-if="!item.isDeleted && item.isExtendedToOriginal && !item.isClipped" 
              class="flex items-center link-pointer mr10">
              <span v-if="item.originalAssetExtendedData && item.isExtendedToOriginal && item.originalAssetExtendedData.previousAssetId && (item.originalAssetExtendedData.filepath || item.originalAssetExtendedData.mergedFilePath) && !cannotMergeBackward"
                class="text-xs btn-carma btn btn-xs mb0"
                :class="{'mr4' :item.originalAssetExtendedData.nextAssetId }"
                @click="handleMergeAssets(item, 'backward')">
                <i class="fa fa-fast-backward link-pointer" aria-hidden="true"></i>
              </span>
              <span v-if="item.originalAssetExtendedData && item.isExtendedToOriginal && item.originalAssetExtendedData.nextAssetId && (item.originalAssetExtendedData.filepath || item.originalAssetExtendedData.mergedFilePath) && !cannotMergeForward"
                class="text-xs btn btn-carma btn-xs mb0"
                @click="handleMergeAssets(item, 'forward')">
                <i class="fa fa-fast-forward link-pointer" aria-hidden="true"></i>
              </span>
              <button
                v-if="item.isExtendedToOriginal && !item.isDeleted && !item.isClipped && !(cannotMergeBackward && cannotMergeForward)"
                class="text-xs btn btn-carma btn-xs mb0 ml4"
                @click="openMultiMergeModal(item)"
                v-tooltip="$t('mediaClipper.merge.mergeMultipleClips')">
                <i class="fa fa-arrows-alt-h link-pointer" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="flex items-center text-xs">
          <div class="mr10 flex items-center">
            <button 
              @click="toggleTimeMode" 
              class="mr10 mb0 btn btn-xs btn-carma inline-block btn-text-xs"
              v-tooltip="isAirtimeMode ? $t('mediaClipper.timeMode.switchToVideoTime') : $t('mediaClipper.timeMode.switchToAirTime')">
              {{ isAirtimeMode ? $t('mediaClipper.timeMode.airTime') : $t('mediaClipper.timeMode.videoTime') }}
            </button>
            
            <span class="ml10 inline-block">
              {{$t('mediaClipper.clippingMetadata.start')}}
            </span>
            <input 
              type="text" 
              v-model="editableStartTime" 
              @blur="updateStartTime"
              @keyup.enter="updateStartTime"
              @keydown="handleInputKeydown"
              class="time-input ml4"
              :placeholder="isAirtimeMode ? 'HH:MM:SS' : 'MM:SS'"
            >
            
            <span class="ml10 inline-block">
              {{$t('mediaClipper.clippingMetadata.end')}}
            </span>
            <input 
              type="text" 
              v-model="editableEndTime" 
              @blur="updateEndTime"
              @keyup.enter="updateEndTime"
              @keydown="handleInputKeydown"
              class="time-input ml4"
              :placeholder="isAirtimeMode ? 'HH:MM:SS' : 'MM:SS'"
            >
          </div>
          <button  
            @click="openConfirmationModal('save', item)" 
            :disabled="(!item.isClipped && !item.isDeleted) || isOverMaxDuration(item)" 
            class="mr10 mb0 btn btn-xs btn-green max-content-width inline-block btn-text-xs"
            v-tooltip="isOverMaxDuration(item) ? $t('mediaClipper.tooltips.saveDisabledOverLimit', { maxDuration: formatMaxDuration() }) : ''">
            {{$t('mediaClipper.clippingMetadata.save')}}
          </button>
        </div>
      </div>
      
      <!-- Multi-Merge Modal -->
      <div v-if="showMultiMerge" class="merge-popup-backdrop" @click.self="showMultiMerge = false">
        <div class="merge-popup">
          <!-- Messages positioned outside modal to avoid overlay transparency -->
          <div class="messages-left">
            <div v-if="shouldShowDurationWarning" class="merge-warning">
              {{ $t('mediaClipper.merge.durationWarning') }}
            </div>

            <div v-if="cannotMerge || multiMergeError" class="merge-error">
              {{ cannotMerge ? $t('mediaClipper.merge.durationLimitExceeded') : multiMergeError }}
            </div>
          </div>

          <h4>{{ $t('mediaClipper.merge.mergeMultipleClips') }}</h4>
          
          <label>
            {{ $t('mediaClipper.merge.clipsBefore') }}
            <span class="available-count">({{ availableClipsBefore }} {{ $t('mediaClipper.merge.available') }})</span>
          </label>
            <input
              type="number"
              v-model.number="multiBefore"
              min="0"
              :max="availableClipsBefore"
              :disabled="availableClipsBefore === 0" />
          
          <label>
            {{ $t('mediaClipper.merge.clipsAfter') }}
            <span class="available-count">({{ availableClipsAfter }} {{ $t('mediaClipper.merge.available') }})</span>
          </label>
            <input
              type="number"
              v-model.number="multiAfter"
              min="0"
              :max="availableClipsAfter"
              :disabled="availableClipsAfter === 0" />
          
          <div class="merge-preview">
            <div class="preview-time">{{ expectedStartTime }} - {{ expectedEndTime }} ({{ expectedDuration }})</div>
          </div>

          <div class="actions">
            <button
              v-if="!cannotMerge"
              class="btn btn-xs btn-carma"
              @click="confirmMultiMerge"
              :disabled="(multiBefore === 0 && multiAfter === 0) || multiBefore > availableClipsBefore || multiAfter > availableClipsAfter">
              {{ $t('mediaClipper.merge.mergeButton') }}
            </button>
            <button class="btn btn-xs btn-danger" @click="showMultiMerge = false">{{ $t('mediaClipper.clippingMetadata.cancel') }}</button>
          </div>
        </div>
      </div>
    </div>
  </template>
  <script>
  
  import { mapActions, mapState } from 'vuex';
  import debounce from 'lodash.debounce'
  import Moment from 'moment';
  
  
  export default {
    data(){
      return{
        progress: 0,
        maxMergeDuration: 9000, // 2.5 hours
        deleteEndThreshold: 1, // If deletion end is within this many seconds of video end, extend to actual end
        isOverlayDisabled: false,
        debouncePreloadKeyframes: null,
        editableStartTime: '',
        editableEndTime: '',
        isAirtimeMode: localStorage.getItem('mediaClipperTimeMode') === 'airtime',
        showMultiMerge: false,
        multiBefore: 0,
        multiAfter: 0,
        multiMergeItem: null,
        multiMergeError: '',
        // Duration limit: 3 hours 15 minutes = 11700 seconds
        MAX_MERGE_DURATION_SECONDS: 11700,
      }
    },
    props: {
      item: {
        type: Object
      },
      currentKeyFrameBlob:{
        type: String
      },
      formatSecondsToTime: {
        type: Function,
        required: true
      },
      videoPlayer: {
        required: true
      },
    },
    mounted() {
      this.showOverlay(true);
      this.updateEditableTimes();
      // Initialize deletion tracking
      if (!this.item.deletedSegments) {
        this.$set(this.item, 'deletedSegments', []);
      }
      if (!this.item.originalTimelineDuration) {
        this.$set(this.item, 'originalTimelineDuration', this.item.duration);
      }
    },
    watch: {
      'item.handles'() {
        this.updateEditableTimes();
      },
      'item.handles.start.position'() {
        this.updateEditableTimes();
      },
      'item.handles.end.position'() {
        this.updateEditableTimes();
      },
      'item.duration'() {
        this.updateEditableTimes();
      },
      isAirtimeMode() {
        this.updateEditableTimes();
      },
      multiBefore(newVal) {
        if (newVal > this.availableClipsBefore) {
          this.multiBefore = this.availableClipsBefore;
        }
      },
      multiAfter(newVal) {
        if (newVal > this.availableClipsAfter) {
          this.multiAfter = this.availableClipsAfter;
        }
      }
    },
    computed: {
      ...mapState({
        articleAssetsData: state => state.mediaClipper.articleAssetsData,
      }),
      cannotMerge() {
        const durationStr = this.expectedDuration;
        if (durationStr === '--:--:--') return false;

        const [hours, minutes, seconds] = durationStr.split(':').map(Number);
        const totalSeconds = hours * 3600 + minutes * 60 + seconds;

        return totalSeconds > this.MAX_MERGE_DURATION_SECONDS;
      },

      cannotMergeBackward() {
        // Check if merging one clip backward would exceed limit
        const totalSeconds = this.calculateDuration(1, 0);
        return totalSeconds > this.MAX_MERGE_DURATION_SECONDS;
      },

      cannotMergeForward() {
        // Check if merging one clip forward would exceed limit
        const totalSeconds = this.calculateDuration(0, 1);
        return totalSeconds > this.MAX_MERGE_DURATION_SECONDS;
      },

      baseAirtime() {
        // code based on public bodhi airtime calculation
        // If extended or merged, use the recording segment's start time
        if (this.item.isExtendedToOriginal || this.item.isMerged) {
          const assetId = this.item.originalAssetExtendedData?.mergedBackwardAssetId || 
                          this.item.originalAssetExtendedData?.currentAssetId;
          
          if (assetId && this.articleAssetsData?.data?.channelRecordings) {
            const recording = this.articleAssetsData.data.channelRecordings.find(
              asset => asset.id === assetId
            );
            
            if (recording && recording.startTime) {
              // Convert UTC startTime to HH:mm:ss format
              const startTime = Moment.utc(recording.startTime);
              return startTime.format('HH:mm:ss');
            }
          }
        }
        
        // Otherwise use the original article airtime
        return this.item.articleAirTime;
      },

      calculateOriginalClipPos() {
        if ((this.item.isExtendedToOriginal || this.item.isMerged) && this.item.articleAirTime  ) {
          const clipAirTime = this.item.articleAirTime;
          const segmentStartTime = Moment.utc(this.articleAssetsData.data.channelRecordings.find(asset => asset.id === (this.item.originalAssetExtendedData.mergedBackwardAssetId  || this.item.originalAssetExtendedData.currentAssetId)).startTime);
          const segmentEndTime = Moment.utc(this.articleAssetsData.data.channelRecordings.find(asset => asset.id === (this.item.originalAssetExtendedData.mergedForwardAssetId || this.item.originalAssetExtendedData.currentAssetId)).endTime);
          const clipLength = this.item.originalAssetUnExtendedData.duration; // seconds
          const clipStartTime = Moment(clipAirTime, "HH:mm:ss");
  
          // Convert times to seconds since start of day
          const segmentStartInSeconds = segmentStartTime.hours() * 3600 + segmentStartTime.minutes() * 60 + segmentStartTime.seconds() + segmentStartTime.milliseconds() / 1000;
          const segmentEndInSeconds = segmentEndTime.hours() * 3600 + segmentEndTime.minutes() * 60 + segmentEndTime.seconds() + segmentEndTime.milliseconds() / 1000;
          const segmentDuration = Math.ceil(segmentEndInSeconds - segmentStartInSeconds);

          const clipStartInSeconds = clipStartTime.hours() * 3600 + clipStartTime.minutes() * 60 + clipStartTime.seconds();
          const clipOffset = clipStartInSeconds - segmentStartInSeconds;  // Offset of the clip within the segment

          // Calculate the clip end time using the clip start time and the clip length
          const clipEndInSeconds = clipStartInSeconds + clipLength;

          // Calculate the percentage of the segment the clip occupies
          let clipStartPercentage = (clipOffset / segmentDuration) * 100;
          let clipEndPercentage = ((clipEndInSeconds - segmentStartInSeconds) / segmentDuration) * 100;

          clipStartPercentage = Math.max(0, parseFloat(clipStartPercentage.toFixed(2)));
          clipEndPercentage = Math.min(100, parseFloat(clipEndPercentage.toFixed(2)));

          this.$nextTick(() => {
            if (!this.$refs.originalClipPosLine) {
              console.error('Original clip pos ref is undefined.');
              return;
            }
            this.$refs.originalClipPosLine.style.setProperty("--start-position", clipStartPercentage);
            this.$refs.originalClipPosLine.style.setProperty("--end-position", clipEndPercentage);
         });
          return {
            clipStartPercentage,
            clipEndPercentage
          };
        }
        return {
          clipStartPercentage: 0,
          clipEndPercentage: 0
        };
      },

      // Counted by the server on load, then drawn down as merges consume recordings.
      availableClipsBefore() {
        return this.multiMergeItem?.originalAssetExtendedData?.availableBefore || 0;
      },

      availableClipsAfter() {
        return this.multiMergeItem?.originalAssetExtendedData?.availableAfter || 0;
      },


      expectedDuration() {
        const totalSeconds = this.calculateDuration(this.multiBefore, this.multiAfter);

        const duration = Moment.duration(totalSeconds, 'seconds');
        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      },

     
      shouldShowDurationWarning() {
        const durationStr = this.expectedDuration;
        if (durationStr === '--:--:--') return false;

        const [hours, minutes, seconds] = durationStr.split(':').map(Number);
        const totalSeconds = hours * 3600 + minutes * 60 + seconds;

        // Warning threshold: 2.5 hours = 9000 seconds
        return totalSeconds > 9000;
      },


      expectedStartTime() {
        return this.calculateExpectedStartTime(this.multiBefore);
      },

      expectedEndTime() {
        return this.calculateExpectedEndTime(this.multiAfter);
      }
    },

    created() {
      this.debouncePreloadKeyframes = debounce((duration) => {
        this.$emit('preloadKeyframesNearPlayhead', duration );
      }, 100)
    },

    methods: {
      calculateDuration(beforeCount, afterCount) {
        // Calculate expected start time with given counts
        const startTimeStr = this.calculateExpectedStartTime(beforeCount);
        const endTimeStr = this.calculateExpectedEndTime(afterCount);

        if (!startTimeStr || !endTimeStr ||
            startTimeStr === '--:--:--' || endTimeStr === '--:--:--') {
          return 0; // Return seconds
        }

        const startTime = Moment.utc(startTimeStr, 'HH:mm:ss');
        let endTime = Moment.utc(endTimeStr, 'HH:mm:ss');

        // Handle crossing midnight: if end time is before start time, add 24 hours
        if (endTime.isBefore(startTime)) {
          endTime.add(24, 'hours');
        }

        return endTime.diff(startTime, 'seconds');
      },

      calculateExpectedStartTime(beforeCount) {
        if (!this.multiMergeItem) return '--:--:--';

        // If merging backward, find the start time of the furthest back clip
        if (beforeCount > 0) {
          const extended = this.multiMergeItem.originalAssetExtendedData;
          if (extended.previousAssetId && this.articleAssetsData?.data?.channelRecordings) {
            let prevId = extended.previousAssetId;
            // Navigate to the furthest back asset we'll merge
            for (let i = 0; i < beforeCount - 1; i++) {
              const asset = this.articleAssetsData.data.channelRecordings.find(a => a.next === prevId);
              if (!asset) break;
              prevId = asset.id;
            }

            // Get the start time of that asset
            const recording = this.articleAssetsData.data.channelRecordings.find(asset => asset.id === prevId);
            if (recording && recording.startTime) {
              const startTime = Moment.utc(recording.startTime);
              return startTime.format('HH:mm:ss');
            }
          }
        }

        // Otherwise, return current start time (base airtime for multiMergeItem)
        const item = this.multiMergeItem;
        if (item.isExtendedToOriginal || item.isMerged) {
          const assetId = item.originalAssetExtendedData?.mergedBackwardAssetId ||
                          item.originalAssetExtendedData?.currentAssetId;

          if (assetId && this.articleAssetsData?.data?.channelRecordings) {
            const recording = this.articleAssetsData.data.channelRecordings.find(
              asset => asset.id === assetId
            );

            if (recording && recording.startTime) {
              const startTime = Moment.utc(recording.startTime);
              return startTime.format('HH:mm:ss');
            }
          }
        }

        // Fallback to article airtime
        if (item.articleAirTime) {
          return item.articleAirTime;
        }
        return '--:--:--';
      },

      calculateExpectedEndTime(afterCount) {
        if (!this.multiMergeItem) return '--:--:--';

        // If merging forward, find the end time of the furthest forward clip
        if (afterCount > 0) {
          const extended = this.multiMergeItem.originalAssetExtendedData;
          if (extended.nextAssetId && this.articleAssetsData?.data?.channelRecordings) {
            let nextId = extended.nextAssetId;
            // Navigate to the furthest forward asset we'll merge
            for (let i = 0; i < afterCount - 1; i++) {
              const asset = this.articleAssetsData.data.channelRecordings.find(a => a.previous === nextId);
              if (!asset) break;
              nextId = asset.id;
            }

            // Get the end time of that asset
            const recording = this.articleAssetsData.data.channelRecordings.find(asset => asset.id === nextId);
            if (recording && recording.endTime) {
              const endTime = Moment.utc(recording.endTime);
              return endTime.format('HH:mm:ss');
            }
          }
        }

        // Otherwise, calculate current end time based on base airtime + duration
        const item = this.multiMergeItem;
        let baseAirtime = null;

        if (item.isExtendedToOriginal || item.isMerged) {
          const assetId = item.originalAssetExtendedData?.mergedBackwardAssetId ||
                          item.originalAssetExtendedData?.currentAssetId;

          if (assetId && this.articleAssetsData?.data?.channelRecordings) {
            const recording = this.articleAssetsData.data.channelRecordings.find(
              asset => asset.id === assetId
            );

            if (recording && recording.startTime) {
              const startTime = Moment.utc(recording.startTime);
              baseAirtime = startTime.format('HH:mm:ss');
            }
          }
        }

        if (!baseAirtime && item.articleAirTime) {
          baseAirtime = item.articleAirTime;
        }

        if (baseAirtime) {
          // Use the actual current duration (which reflects any previous merges)
          // originalTimelineDuration should be set after merges, but fallback to duration
          const currentDuration = item.originalTimelineDuration || item.duration || 0;
          // Calculate end time: baseAirtime + duration
          const date = new Date(`1970-01-01T${baseAirtime}`);
          date.setSeconds(date.getSeconds() + currentDuration);
          return date.toTimeString().slice(0, 8);
        }

        return '--:--:--';
      },

      ...mapActions({
        ffprobeMediaDuration: "mediaClipper/ffprobeMediaDuration",
        mergeArticleAssets: "mediaClipper/mergeArticleAssets",
        deleteSection: "mediaClipper/deleteSection",
        clipSection: "mediaClipper/clipSection",
      }),

      /* ====================================================================
       * TIMELINE COORDINATE SYSTEM - EXAMPLES
       * ====================================================================
       * 
       * We track deletions in the ORIGINAL timeline to maintain consistency
       * across multiple edits. Here's how it works:
       * 
       * EXAMPLE 1: Simple Deletion
       * ---------------------------
       * Original video: 0-600s (10 minutes)
       * User deletes: 8:00-9:00 (480-540s in current video)
       * 
       *   deletedSegments: [{startTime: 480, endTime: 540}],
       * 
       * New video: 0-540s (9 minutes, physically cropped by server)
       * 
       * EXAMPLE 2: Multiple Deletions
       * ------------------------------
       * Original video: 0-600s
       * First deletion: 5:00-6:00 (300-360s) → video becomes 540s
       * Second deletion: 8:00-9:00 in NEW video (480-540s in NEW video)
       * 
       * To find where 480s in NEW video maps to ORIGINAL timeline:
       * - 480s in new video
       * - Add back first deletion (60s): 480 + 60 = 540s in original
       * 
       *   deletedSegments: [
       *     {startTime: 300, endTime: 360},
       *     {startTime: 540, endTime: 600}
       *   ],
       *   originalTimelineDuration: 600
       * }
       * 
       * EXAMPLE 3: Extended/Merged Video
       * ---------------------------------
       * Original clip: 0-600s (10 min segment)
       * User extends: Adds next 10-min segment → 0-1200s
       * User deletes: 9:00-11:00 (540-660s)
       *
       *   deletedSegments: [{startTime: 540, endTime: 660}],
       *   originalTimelineDuration: 1200  ← Grows when extending!
       * 
       * - currentToOriginalTime(): Maps current video time → original timeline
       * - originalToCurrentTime(): Maps original timeline → current video time
       * ==================================================================== */

      updateEditableTimes() {
        if (this.isAirtimeMode) {
          // Show airtime format
          this.editableStartTime = this.videoTimeToAirtime(this.item.handles.start.time);
          this.editableEndTime = this.videoTimeToAirtime(this.item.handles.end.time);
        } else {
          // Show video time format
          this.editableStartTime = this.formatHandleTooltipTime(this.item.duration, this.item.handles.start.position);
          this.editableEndTime = this.formatHandleTooltipTime(this.item.duration, this.item.handles.end.position);
        }
      },

      toggleTimeMode() {
        this.isAirtimeMode = !this.isAirtimeMode;
        localStorage.setItem('mediaClipperTimeMode', this.isAirtimeMode ? 'airtime' : 'video');
      },

      handleInputKeydown(event) {
        // Only stop propagation for arrow keys when editing time inputs
        // This allows them to move cursor in input without affecting video
        const arrowKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (arrowKeys.indexOf(event.key) !== -1) {
          event.stopPropagation();
        }
      },

      videoTimeToAirtime(videoSeconds) {
        if (!this.baseAirtime) return '';
        // Map current video time to original timeline time
        const originalSeconds = this.currentToOriginalTime(this.item, videoSeconds);
        
        const date = new Date(`1970-01-01T${this.baseAirtime}`);
        date.setSeconds(date.getSeconds() + originalSeconds);
        
        return date.toTimeString().slice(0, 8);
      },

      airtimeToVideoTime(airtimeString) {
        if (!this.baseAirtime || !airtimeString) return null;
        
        const baseDate = new Date(`1970-01-01T${this.baseAirtime}`);
        const inputDate = new Date(`1970-01-01T${airtimeString}`);
        
        let diffSeconds = (inputDate - baseDate) / 1000;
        
        const originalTimelineDuration = this.item.originalTimelineDuration || this.item.duration;
        
        // Handle day rollover (video crosses midnight)
        // If diff is negative, it means the input time is "before" the base time in clock terms
        // This could mean it's actually the next day
        // Check if adding 24 hours would put it within the video duration
        if (diffSeconds < 0) {
          const diffWithRollover = diffSeconds + 86400; // Add 24 hours
          if (diffWithRollover >= 0 && diffWithRollover <= originalTimelineDuration) {
            diffSeconds = diffWithRollover;
          }
        }
        
        // Validate against original timeline
        if (diffSeconds < 0 || diffSeconds > originalTimelineDuration) {
          return null;
        }
        
        // Map original timeline time to current video time
        const compressedSeconds = this.originalToCurrentTime(this.item, diffSeconds);
        
        // Validate compressed time
        if (compressedSeconds < 0 || compressedSeconds > this.item.duration) {
          return null;
        }
        
        return compressedSeconds;
      },
      alert(toTranslateError){
          this.$notify({
            group: "store_notifications",
            type: "error",
            text: this.$t(toTranslateError)
          });
      },
      updateStartTime() {
        let startSeconds;
        
        if (this.isAirtimeMode) {
          startSeconds = this.airtimeToVideoTime(this.editableStartTime);
          if (startSeconds === null) {
            this.alert('mediaClipper.validation.invalidAirtimeFormat');
            this.updateEditableTimes();
            return;
          }
        } else {
          startSeconds = this.parseTimeToSeconds(this.editableStartTime);
          if (startSeconds === null) {
            this.alert('mediaClipper.validation.invalidTimeFormat');
            this.updateEditableTimes();
            return;
          }
        }
        
        const endSeconds = this.item.handles.end.time;
        
        if (startSeconds >= endSeconds) {
          this.alert('mediaClipper.validation.startBeforeEnd');
          this.updateEditableTimes();
          return;
        }
        
        if (startSeconds > this.item.duration) {
          this.alert('mediaClipper.validation.startExceedsDuration');
          this.updateEditableTimes();
          return;
        }
        
        const startPosition = (startSeconds / this.item.duration) * 100;
        this.item.handles.start.position = startPosition;
        this.item.handles.start.time = startSeconds;
      },

      updateEndTime() {
        let endSeconds;
        
        if (this.isAirtimeMode) {
          endSeconds = this.airtimeToVideoTime(this.editableEndTime);
          if (endSeconds === null) {
            this.alert('mediaClipper.validation.invalidAirtimeFormat');
            this.updateEditableTimes();
            return;
          }
        } else {
          endSeconds = this.parseTimeToSeconds(this.editableEndTime);
          if (endSeconds === null) {
            this.alert('mediaClipper.validation.invalidTimeFormat');
            this.updateEditableTimes();
            return;
          }
        }
        
        const startSeconds = this.item.handles.start.time;
        
        if (endSeconds <= startSeconds) {
          this.alert('mediaClipper.validation.endAfterStart');
          this.updateEditableTimes();
          return;
        }
        
        if (endSeconds > this.item.duration) {
          this.alert('mediaClipper.validation.endExceedsDuration');
          this.updateEditableTimes();
          return;
        }
        
        const endPosition = (endSeconds / this.item.duration) * 100;
        this.item.handles.end.position = endPosition;
        this.item.handles.end.time = endSeconds;
      },

      parseTimeToSeconds(timeString) {
        if (!timeString) return null;
        
        const parts = timeString.split(':');
        let seconds = 0;
        
        if (parts.length === 2) {
          // MM:SS format
          const minutes = parseInt(parts[0]);
          const secs = parseInt(parts[1]);
          if (isNaN(minutes) || isNaN(secs)) return null;
          seconds = minutes * 60 + secs;
        } else if (parts.length === 3) {
          // HH:MM:SS format
          const hours = parseInt(parts[0]);
          const minutes = parseInt(parts[1]);
          const secs = parseInt(parts[2]);
          if (isNaN(hours) || isNaN(minutes) || isNaN(secs)) return null;
          seconds = hours * 3600 + minutes * 60 + secs;
        } else {
          return null;
        }
        
        return seconds;
      },
      calculateAirtimeWithVideoOffset(progressPercent = this.progress) {
        if (!this.baseAirtime) return '';

        const videoOffset = Math.floor((this.item.duration * progressPercent) / 100);
        const originalSeconds = this.currentToOriginalTime(this.item, videoOffset);
        const date = new Date(`1970-01-01T${this.baseAirtime}`);
        date.setSeconds(date.getSeconds() + originalSeconds);

        return date.toTimeString().slice(0, 8);
      },

      async handleClipping(item){
        this.showLoader();
        // Initialize tracking
        if (!item.originalTimelineDuration) {
          item.originalTimelineDuration = item.duration;
        }
        if (!item.deletedSegments) {
          item.deletedSegments = [];
        }
        let src;
        if (item.isClipped) {
          src = item.clippedAssetObj.filepath; // If already clipped, use the updated clipped path
        } else if (item.isExtendedToOriginal && !item.isMerged) {
          src = item.originalAssetExtendedData.filepath; // If extended to original, and not merged
        } else if (item.isMerged) {
          src = item.originalAssetExtendedData.mergedFilePath; // If merged after extending
        } else {
          src = item.src; // Default to original src
        }
        const startSeconds = Math.floor(item.handles.start.time);
        const endSeconds = Math.floor(item.handles.end.time);
        // Convert compressed video times to original timeline times
        const originalStart = this.currentToOriginalTime(item, startSeconds);
        
        let data = {
          startTime: startSeconds,
          endTime: endSeconds,
          src,
          mimeType: item.mimeType,
          isLocal : item.isMerged || item.isExtendedToOriginal || item.isDeleted || item.isClipped,
        }
        
        try{
          const clippedSegment = await this.clipSection(data);
          if (!clippedSegment) throw new Error('Clipped Segment not found')
          item.src = clippedSegment.src;
          item.clippedAssetObj.filepath = clippedSegment.filepath;
          item.clippedStartOffset = startSeconds;
          
          // Track what was DELETED during clipping (in original timeline)
          const newDeletedSegments = [];
          
          // Delete segment BEFORE the clip (if any)
          if (originalStart > 0) {
            newDeletedSegments.push({
              startTime: 0,
              endTime: originalStart
            });
          }
          
          // Merge with existing deleted segments
          item.deletedSegments = this.mergeDeletedSegments([
            ...item.deletedSegments,
            ...newDeletedSegments
          ]);
          
          item.airtimeToCompute = this.getAirTimeData(item, 'clip');
          item.lastAction = 'clip';
          this.resetTimeline(item, endSeconds - startSeconds, 'clipSection');
        }catch(e){
          this.$emit('reloadMediaPlayer', true);
          console.log(e)
        }
      },

      /**
       * Maps current video time to original timeline time
       * 
       * Example:
       * - Original video: 0-600s
       * - Deleted: 300-360s (1 minute removed)
       * - Current video: 0-540s (server returned cropped video)
       * 
       * If user is at 400s in current video:
       * - Check deletions before 400s: one deletion (300-360s)
       * - Add back deleted duration: 400 + 60 = 460s
       * - Returns: 460s (position in original timeline)
       * 
       * Why? When we delete again, we need to know where in the
       * ORIGINAL timeline we're deleting, not the compressed timeline.
       */
      currentToOriginalTime(item, currentTime) {
        if (!item.deletedSegments || item.deletedSegments.length === 0) {
          return currentTime;
        }
        
        // Sort deletions by start time in original timeline
        const sortedDeletions = [...item.deletedSegments].sort((a, b) => a.startTime - b.startTime);
        
        let originalTime = currentTime;
        let accumulatedOffset = 0;
        
        for (let i = 0; i < sortedDeletions.length; i++) {
          const seg = sortedDeletions[i];
          const segDuration = seg.endTime - seg.startTime;
          
          // Where does this deletion start in the current video?
          const deletionStartInCurrent = seg.startTime - accumulatedOffset;
          
          if (currentTime >= deletionStartInCurrent) {
            // Current position is after this deletion, add its duration
            originalTime += segDuration;
            accumulatedOffset += segDuration;
          } else {
            // Current position is before this deletion, we're done
            break;
          }
        }
        
        return originalTime;
      },

      /**
       * Maps original timeline time to current video time
       * 
       * Example:
       * - Original video: 0-600s
       * - Deleted: 300-360s (1 minute removed)
       * - Current video: 0-540s
       * 
       * If we want to find 460s from original timeline:
       * - Check deletions before 460s: one deletion (300-360s)
       * - Subtract deleted duration: 460 - 60 = 400s
       * - Returns: 400s (position in current video)
       * 
       * Used for: Converting airtime input back to video position
       */
      originalToCurrentTime(item, originalTime) {
        if (!item.deletedSegments || item.deletedSegments.length === 0) {
          return originalTime;
        }
        
        let currentTime = originalTime;
        
        // Subtract all deleted durations that come before this point
        for (let i = 0; i < item.deletedSegments.length; i++) {
          const seg = item.deletedSegments[i];
          const segDuration = seg.endTime - seg.startTime;
          
          if (seg.endTime <= originalTime) {
            currentTime -= segDuration;
          } else if (seg.startTime < originalTime && originalTime < seg.endTime) {
            // Original time is inside a deleted segment
            // Snap to the start of the deletion
            currentTime = seg.startTime;
            // Subtract all previous deletions
            for (let j = 0; j < i; j++) {
              const prevSeg = item.deletedSegments[j];
              currentTime -= (prevSeg.endTime - prevSeg.startTime);
            }
            break;
          }
        }
        
        return Math.max(0, currentTime);
      },

      mergeDeletedSegments(segments) {
        if (segments.length === 0) return [];
        
        const sorted = [...segments].sort((a, b) => a.startTime - b.startTime);
        const merged = [sorted[0]];
        
        for (let i = 1; i < sorted.length; i++) {
          const current = sorted[i];
          const last = merged[merged.length - 1];
          
          if (current.startTime <= last.endTime) {
            // Overlapping or adjacent - merge
            last.endTime = Math.max(last.endTime, current.endTime);
          } else {
            merged.push(current);
          }
        }
        
        return merged;
      },

      /**
       * Handles deletion of a section from the video
       * 
       * IMPORTANT: Always store deletions in ORIGINAL timeline coordinates!
       * 
       * Example Flow:
       * 1. User selects 8:00-9:00 in current 540s video
       * 2. Convert to original timeline: 480s → 540s (using currentToOriginalTime)
       * 3. Store: {startTime: 540, endTime: 600} in original coordinates
       * 4. Server returns new 480s video (60s removed)
       * 5. Next deletion uses currentToOriginalTime to map correctly
       */
      async handleDeleteSection(item) {
        this.showLoader();
        
        // Initialize tracking if not exists
        if (!item.originalTimelineDuration) {
          item.originalTimelineDuration = item.duration;
        }
        if (!item.deletedSegments) {
          item.deletedSegments = [];
        }
        
        // These are in CURRENT compressed video time (what the handles show)
        const startSeconds = Math.floor(item.handles.start.time);
        let endSeconds = Math.floor(item.handles.end.time);
        
        // Track if deletion extends to the end (before converting to original time)
        const extendsToEnd = item.duration - endSeconds <= this.deleteEndThreshold;
        
        // If deletion end is very close to video end (within threshold), extend to actual end
        // This handles cases where user might not be perfectly accurate when selecting the end
        if (extendsToEnd) {
          endSeconds = item.duration;
        }
        
        // Convert to ORIGINAL timeline coordinates BEFORE storing
        const originalStartSeconds = this.currentToOriginalTime(item, startSeconds);
        const originalEndSeconds = this.currentToOriginalTime(item, endSeconds);
        
        const startTime = this.formatSecondsToTime(startSeconds, true);
        const endTime = this.formatSecondsToTime(endSeconds, true);
        
        let src;
        // RULES OF DELETION - IF WE DELETE WE CAN'T CLIP NOR EXTEND NOR MERGE
        if (item.isDeleted) {
          src = item.deletedAssetObj.filepath; // If already deleted, use filepath of deleted
        } else if (item.isClipped) {
          src = item.clippedAssetObj.filepath; // If clipped, use the updated clipped path
        } else if (item.isExtendedToOriginal && !item.isMerged) {
          src = item.originalAssetExtendedData.filepath; // If extended to original, not merged
        } else if (item.isMerged) {
          src = item.originalAssetExtendedData.mergedFilePath; // If merged after extending
        } else {
          src = item.src; // Default to original src
        }
        const data = {
          path: src,
          isLocal: item.isMerged || item.isExtendedToOriginal || item.isDeleted || item.isClipped,
          startTime,
          endTime,
        }
        try {
          const deletedSegment = await this.deleteSection(data);
          if (!deletedSegment) throw new Error('Deleted Segment not found')
          if (!item.isDeleted) item.isDeleted = true;
          item.src = deletedSegment.src;
          item.deletedAssetObj.filepath = deletedSegment.filepath;
          // Store deletion in ORIGINAL timeline coordinates (not current!)
          // Note: We don't store deletions that extend to the end of the video
          // because they're not needed for time conversion - positions in the current video
          // never reach beyond the end, so that deletion is irrelevant
          if (!extendsToEnd) {
            item.deletedSegments.push({ 
              startTime: originalStartSeconds, 
              endTime: originalEndSeconds 
            });
            // Sort and merge overlapping segments
            item.deletedSegments = this.mergeDeletedSegments(item.deletedSegments);
          }  
          item.airtimeToCompute = this.getAirTimeData(item, 'delete');
          item.lastAction = 'delete';
          this.resetTimeline(item, deletedSegment.totalDuration, 'deleteSection');
        }catch(e){
          this.$emit('reloadMediaPlayer', true);
          console.log(e)
        }
      },

      getMediaFormat(item) {
        return item.originalAssetExtendedData?.extension || null;
      },

      archiveFilepath(assetId) {
        const recording = (this.articleAssetsData?.data?.channelRecordings || [])
          .find(asset => asset.id === assetId);
        return recording ? recording.filepath : null;
      },

      // Helper: Get current file path (merged or original)
      getCurrentFilePath(item) {
        const extended = item.originalAssetExtendedData;
        return item.isMerged ? extended.mergedFilePath : extended.filepath;
      },

      // Helper: Build merge files array
      buildMergeFiles(item, before, after) {
        const extended = item.originalAssetExtendedData;
        const previousFiles = [];
        const nextFiles = [];
        let furthestBackMergedId = null;
        let finalNextId = null;

        // Collect previous files (in reverse order - furthest first)
        let prevId = extended.previousAssetId;
        for (let i = 0; i < before; i++) {
          if (!prevId) break;
          const prevPath = this.archiveFilepath(prevId);
          if (!prevPath) break;
          previousFiles.unshift({
            block: `previous_${i}`,
            type: 'localfile',
            location: prevPath
          });
          furthestBackMergedId = prevId;
          const asset = this.articleAssetsData.data.channelRecordings.find(a => a.next === prevId);
          prevId = asset ? asset.id : null;
        }

        // Collect next files
        let nextId = extended.nextAssetId;
        for (let i = 0; i < after; i++) {
          if (!nextId) break;
          const nextPath = this.archiveFilepath(nextId);
          if (!nextPath) break;
          nextFiles.push({
            block: `next_${i}`,
            type: 'localfile',
            location: nextPath
          });
          finalNextId = nextId;
          const asset = this.articleAssetsData.data.channelRecordings.find(a => a.previous === nextId);
          nextId = asset ? asset.id : null;
        }

        // Build complete files array: [prevN, ..., prev1, current, next1, ..., nextN]
        const currentPath = this.getCurrentFilePath(item);
        const allFiles = [
          ...previousFiles,
          {
            block: 'current',
            type: 'localfile',
            location: currentPath
          },
          ...nextFiles
        ];

        return { allFiles, furthestBackMergedId, finalNextId };
      },

      // Helper: Update asset IDs after merge
      updateAssetIdsAfterMerge(item, before, after, furthestBackMergedId, finalNextId) {
        const extended = item.originalAssetExtendedData;

        extended.availableBefore = Math.max(0, (extended.availableBefore || 0) - before);
        extended.availableAfter = Math.max(0, (extended.availableAfter || 0) - after);

        if (before > 0) {
          // mergedBackwardAssetId should point to the furthest back asset we merged (for baseAirtime calculation)
          extended.mergedBackwardAssetId = furthestBackMergedId || extended.previousAssetId;
          
          // Find the asset before the furthest back one we merged
          if (furthestBackMergedId) {
            const previousAsset = this.articleAssetsData.data.channelRecordings.find(
              asset => asset.next === furthestBackMergedId
            );
            extended.previousAssetId = previousAsset ? previousAsset.id : null;
          }
        }

        if (after > 0 && finalNextId !== undefined) {
          const asset = this.articleAssetsData.data.channelRecordings.find(
            a => a.previous === finalNextId
          );
          if (asset) {
            extended.mergedForwardAssetId = extended.nextAssetId;
            extended.nextAssetId = asset.id;
          } else {
            extended.nextAssetId = null;
          }
        }
      },

      // Helper: Update item properties after merge
      updateItemAfterMerge(item, merged) {
        item.src = merged.mergedFile.src;
        item.originalAssetExtendedData.src = merged.mergedFile.src;
        item.originalAssetExtendedData.mergedFilePath = merged.mergedFile.filepath;
        item.originalAssetExtendedData.duration = item.duration;
        item.originalTimelineDuration = merged.totalDuration;
        if (!item.isMerged) item.isMerged = true;
        this.resetTimeline(item, merged.totalDuration, 'merge');
      },

      // Unified merge method (used by both single and multi-merge)
      async performMerge(item, before, after) {
        if (!item.originalAssetExtendedData.filepath) {
          throw new Error(this.$t('mediaClipper.merge.extendableMediaPathNotFound'));
        }

        const mediaFormat = this.getMediaFormat(item);
        if (!mediaFormat) {
          throw new Error(this.$t('mediaClipper.merge.unexpectedMimeType', { mimeType: item.mimeType }));
        }

        // Build merge files
        const { allFiles, furthestBackMergedId, finalNextId } = this.buildMergeFiles(
          item, before, after
        );

        // Validate we have enough clips
        if (before > 0 && !furthestBackMergedId) {
          const available = allFiles.filter(f => f.block.startsWith('previous')).length;
          throw new Error(this.$t('mediaClipper.merge.onlyPreviousClipsAvailable', { available, requested: before }));
        }
        if (after > 0 && !finalNextId) {
          const available = allFiles.filter(f => f.block.startsWith('next')).length;
          throw new Error(this.$t('mediaClipper.merge.onlyNextClipsAvailable', { available, requested: after }));
        }

        // Perform merge
        const merged = await this.mergeArticleAssets({
          files: allFiles,
          mediaFormat
        });

        if (!merged || !merged.mergedFile) {
          throw new Error(this.$t('mediaClipper.merge.mergeFailed'));
        }

        // Update asset IDs
        this.updateAssetIdsAfterMerge(item, before, after, furthestBackMergedId, finalNextId);

        // Update item properties
        this.updateItemAfterMerge(item, merged);

        return merged;
      },

      // Single merge (backward or forward)
      async handleMergeAssets(item, direction) {
        this.showLoader();
        const before = direction === 'backward' ? 1 : 0;
        const after = direction === 'forward' ? 1 : 0;

        try {
          await this.performMerge(item, before, after);
        } catch (e) {
          this.$emit('reloadMediaPlayer', true);
          console.error('Merge error:', e);
        }
      },

      handleExtendAsset(item){
        this.showLoader();
        item.src = item.originalAssetExtendedData.src;
        item.originalTimelineDuration = item.originalAssetExtendedData.duration;
        item.duration = item.originalAssetExtendedData.duration;
        this.resetTimeline(item, item.originalAssetExtendedData.duration, 'extend');
      },

      openMultiMergeModal(item) {
        this.multiMergeItem = item;
        this.multiBefore = 0;
        this.multiAfter = 0;
        this.multiMergeError = '';
        this.showMultiMerge = true;
      },

      async confirmMultiMerge() {
        const item = this.multiMergeItem;
        if (!item) return;
        
        this.multiMergeError = '';

        const before = this.multiBefore;
        const after = this.multiAfter;

        if (before === 0 && after === 0) {
          this.multiMergeError = this.$t('mediaClipper.merge.specifyAtLeastOneClip');
          return;
        }

        this.showMultiMerge = false;
        this.showLoader();

        try {
          await this.performMerge(item, before, after);
        } catch (e) {
          this.$emit('reloadMediaPlayer', true);
          this.multiMergeError = e.message || this.$t('mediaClipper.merge.errorDuringMerge');
          this.showMultiMerge = true; // Reopen modal to show error
          console.error('Multi-merge error:', e);
        }
      },

      getAirTimeData(item, action) {
        if (!item) throw new Error('Calculate New Air Time - No Valid asset found');
        const isDeletingFromStart = action === 'delete' && Math.floor(item.handles.start.time) === 0;

        const referenceTime = isDeletingFromStart ? item.handles.end.time : 
              (action === 'delete' && item.lastAction === 'clip') ? item.clippedStartOffset : 
              item.handles.start.time;
        const assetId = item.originalAssetExtendedData?.mergedBackwardAssetId || item.originalAssetExtendedData?.currentAssetId;


        const airtimeData = {
          isExtended: item.isExtendedToOriginal,
          referenceTime,
          assetId,
          originalAirtime: item.articleAirTime
        }

        return airtimeData;
      },

      showLoader(){
        this.$emit('showPlayerLoader');
        this.$refs.timelineLine.style.setProperty("--progress-position", 0);
      },

      showOverlay(state){
        this.isOverlayDisabled = state;
      },

      resetTimeline(item, duration, action) {
        if (action === 'clipSection') {
          if (!item.isClipped) item.isClipped = true;
        }
        if (action === 'deleteSection') {
          if (!item.isDeleted) item.isDeleted = true;
        }
        if (action === 'extend') {
          if (!item.isExtendedToOriginal) item.isExtendedToOriginal = true;
        }
        if (action === 'merge') {
          if (!item.isMerged) item.isMerged = true;
        }
        item.duration = duration;
        if (this.$refs.previewImg && this.$refs.previewImg.src) {
          this.$refs.previewImg.src = '';
        }
        item.keyframes = [];
        // The media source changed, so previous handle percentages no longer map, select the whole new timeline
        item.handles = {
          start: {
            time: 0,
            position: 0,
          },
          end: {
            time: duration,
            position: 100,
          },
        };
        item.handleLeft = 0;
        item.currentPlaybackTime = 0;
        this.$emit('reloadMediaPlayer', true);
      },

      rewindCurrentPlaybackTime(item){
        this.$emit('rewindVideoTime', item);
      },

      fastForwardCurrentPlaybackTime(item){
        this.$emit('fastForwardVideoTime', item);
      },

      openConfirmationModal(actionType, item){
        // Block save if over max duration
        if (actionType === 'save' && this.isOverMaxDuration(item)) {
          return; // Don't open modal if over limit
        }
        this.$emit('openConfirmationModal', actionType, item)
      },

      handleTimelineKeyframeUpdate(event, item) {
        this.$emit('isTimelineHovering', true)
        event.preventDefault();
        if(event.currentTarget !== this.$refs.timelineContainer) return;
        const timeline = event.currentTarget;
        const bounds = timeline.getBoundingClientRect();
        const xPosition = event.clientX - bounds.left;
        const yPosition = event.clientY - bounds.top;
        if (yPosition < 0) {
          this.$refs.previewImgContainer.classList.remove('preview-img-container-hovered');
          return;
        }
        this.$refs.previewImgContainer.classList.add('preview-img-container-hovered');
        const progress = Math.min(Math.max(0, xPosition / bounds.width), 1);
        this.progress = progress * 100;
        
        // limit the preview to now create a scroll that make everything jumping
        timeline.style.setProperty("--preview-position", Math.min(progress, 1 - (this.$refs.previewImgContainer.offsetWidth / (2 * bounds.width)) + (50 / bounds.width)));
        this.debouncePreloadKeyframes(Math.floor(progress * item.duration))
        this.$emit('displayLoadedKeyframe', Math.floor(progress * item.duration));
      },

      handleTimelineKeyframeUpdateLeave(event) {
        this.$emit('isTimelineHovering', false)
        event.preventDefault();
        this.$refs.previewImgContainer.classList.remove('preview-img-container-hovered');
        return;
      },

      handlePlayPauseVideo(item){
        if (item.isPlaying){
          this.pauseMedia()
        }else{
          this.playMedia()
        }
      },

      playMedia(){
          this.$emit('playMedia')
      },

      pauseMedia(){
          this.$emit('pauseMedia')
      },

      updatePlaybackHandlePosition(item, playbackTime, newTime) {
        this.$emit('requestVideoPlayer');
        // item.duration is floored, so the player reports times past it near the end
        const progress = Math.max(0, Math.min(1, (newTime || playbackTime) / item.duration));
        this.$refs.timelineLine.style.setProperty("--progress-position", progress);
        item.currentPlaybackTime = newTime || playbackTime;
        item.handleLeft = progress * 100;
      },

      startPlaybackHandleDrag(e, item) {
        if ( e.target.classList.contains('trim-start-handle') || e.target.classList.contains('trim-end-handle')) {
          return;
        }
        const trimContainer = this.$refs.timelineLine.getBoundingClientRect();
        const onMouseMove = (e) => {
          const xTrimContainer = e.clientX - trimContainer.left;
          let progress = xTrimContainer / trimContainer.width;
          progress = Math.max(0, Math.min(1, progress)); // cap progress to max duration / bounds
          const newTime = progress * item.duration;
          item.currentPlaybackTime = newTime;
          item.handleLeft = progress * 100;
          this.$refs.timelineLine.style.setProperty("--progress-position", progress);
          this.$emit('setPlayerActiveVideoTime', newTime);
        };
        const onMouseDown = (e) => {
          const xTrimContainer = e.clientX - trimContainer.left;
          let progress = xTrimContainer / trimContainer.width;
          progress = Math.max(0, Math.min(1, progress)); // cap progress to max duration / bounds
          const newTime = progress * item.duration;
          item.currentPlaybackTime = newTime;
          item.handleLeft = progress * 100;
          this.$refs.timelineLine.style.setProperty("--progress-position", progress);
          this.$emit('setPlayerActiveVideoTime', newTime);
        };
        const onMouseUp = (e) => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          document.removeEventListener('mousedown', onMouseDown);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousedown', onMouseDown);
      },

      startTrimHandlesDrag(type, item) {
        const trimContainer = this.$refs.timelineLine.getBoundingClientRect();
        const minDifference = this.getMinHandlesGap(item, trimContainer.width);
  
        const onMouseMove = (e) => {
          const xTrimContainer = e.clientX - trimContainer.left;
          let progress = (xTrimContainer / trimContainer.width) * 100;
          progress = Math.max(0, Math.min(100, progress));
  
          if (type === 'start') {
            if (progress > item.handles.end.position - minDifference) {
              item.handles.end.position = Math.min(100, progress + minDifference);
              item.handles.end.time =  Math.floor((item.handles.end.position / 100) * item.duration);
            }
            item.handles.start.position = Math.min(progress, item.handles.end.position - minDifference);
            item.handles.start.time = Math.floor((item.handles.start.position / 100) * item.duration);
          } else if (type === 'end') {
            if (progress < item.handles.start.position + minDifference) {
              item.handles.start.position = Math.max(0, progress - minDifference);
              item.handles.start.time = Math.floor((item.handles.start.position / 100) * item.duration);
            }
            item.handles.end.position = Math.max(progress, item.handles.start.position + minDifference);
            item.handles.end.time =  Math.floor((item.handles.end.position / 100) * item.duration);
          }
        };
  
        const onMouseUp = () => {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      },

      handleTrimHandleDoubleClick(item){
        const trimContainer = this.$refs.timelineLine.getBoundingClientRect();
        const selectionWidth = this.getMinHandlesGap(item, trimContainer.width) * 2;
        const progress = item.handleLeft;
        
        item.handles.start.position = Math.max(0, Math.min(progress, 100 - selectionWidth));
        item.handles.start.time = Math.floor((item.handles.start.position / 100) * item.duration);
        item.handles.end.position = Math.min(100, item.handles.start.position + selectionWidth);
        item.handles.end.time =  Math.floor((item.handles.end.position / 100) * item.duration);
      },

      getMinHandlesGap(item, timelineWidth) {
        const handleWidthPercent = (7 / timelineWidth) * 100;
        const minSelectionSeconds = Math.min(5, item.duration / 2);
        const gap = (minSelectionSeconds / item.duration) * 100 + handleWidthPercent;
        return Math.min(gap, 50);
      },

      formatHandleTooltipTime(duration, position) {
        let timeInSeconds = (position / 100) * duration;
        return this.formatSecondsToTime(Math.floor(timeInSeconds));
      },

      getFormattedTimeRange(item) {
        let startTime, endTime;
        
        if (this.isAirtimeMode) {
          // Show air time
          startTime = this.videoTimeToAirtime(item.currentPlaybackTime);
          endTime = this.videoTimeToAirtime(item.duration);
        } else {
          // Show video time
          startTime = this.formatSecondsToTime(item.currentPlaybackTime);
          endTime = this.formatSecondsToTime(item.duration);
        }
        
        return `${startTime} - ${endTime}`;
      },

      formatMaxDuration() {
        const hours = Math.floor(this.maxMergeDuration / 3600);
        const minutes = Math.floor((this.maxMergeDuration % 3600) / 60);
        if (hours > 0) {
          return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
        }
        return `${minutes} minute${minutes > 1 ? 's' : ''}`;
      },

      isOverMaxDuration(item) {
        return item.duration > this.maxMergeDuration;
      }
    }
  }
  </script>
  
  <style lang="scss" scoped>
    $background-dark: #151515;
    $background-darker: #202020;
    $background-medium: #999;
    $text-color-light: #e5e5e5;
    $line-height: 2px;

    .messages-left {
      position: absolute;
      left: -280px;      /* place les messages à gauche du popup */
      top: 0;
      width: 260px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      pointer-events: none;
      z-index: 20;
    }

    .merge-warning,
    .merge-error {
      padding: 14px 16px;
      border-radius: 8px;
      font-size: 14px;
      line-height: 1.45;
      color: #fff;
      opacity: 1 !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
      box-shadow: 0 6px 14px rgba(0,0,0,0.4);
    }

    .merge-warning {
      background: #ff9800 !important;
    }

    .merge-error {
      background: #ef5350 !important;
    }

    .btn-text-xs{
      font-size: 12px;
    }
    .disabled-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(211, 211, 211, 0.5);
      z-index: 999;
      cursor: wait;
    }
    .clip-timelines {
      color: $text-color-light;
      background-color: #151515;
      display: flex;
      align-items: center;
      height: 60px;
      width: 100%;
  
      .timeline-controls {
        margin-right: .5rem;
        
        .time-range-display {
          white-space: nowrap;
        }
      }
  
      .timeline-container {
        z-index: 10;
        width: 100%;
        position: relative;
        display: flex;
        align-items: center;
        height: 25px;
  
        .preview-img-container{
          position: absolute;
          display: none;
          top: -.25rem;
          left: calc(var(--preview-position) * 100%);
          transform: translate(-50%, -100%);
          border: 2px solid white;
          border-radius: .25rem;
          width: max-content;
          background: #fff;
          .preview-img {
            aspect-ratio: 16 / 9;
          }
        }
  
        .preview-img-time-tooltip{
          background:$background-darker ;
          width: 100%;
          text-align: center;
        }
  
        .preview-img-container-hovered {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center
        }
  
        .timeline {
          width: 100%;
          height: 100%;
          position: relative;
          display: flex;
          align-items: center;
  
          .timeline-line {
            position: relative;
            width: 100%;
            height: $line-height;
            background: $background-medium;
  
            &::after {
              content: "";
              position: absolute;
              top: 0;
              left: 0;
              bottom:0;
              right: calc( 100% - var(--progress-position) * 100%);
              background: red;
              height: 100%;
            }
            .original-clip-pos-line {
              position: absolute;
              top: 0;
              left: calc(var(--start-position) * 1%);
              width: calc((var(--end-position) - var(--start-position)) * 1%);
              height: 50%;
              background: $orange;
              z-index: 10;
            }

          }
        }
  
        .handle-wrapper {
          position: absolute;
          transform: translateX(-7.5px);
          cursor: pointer;
          width: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 20;
  
          #handle {
            position: relative;
            border-radius: 50%;
            width: .5rem;
            height: .5rem;
            pointer-events: auto;
            background: red;
          }
        }

        .original-start-end-marker{
          position: absolute;
          bottom: 0;
          border-radius: 1rem;
          top: 38.75%;
          width: 2px;
          height: 22.5%;
          z-index: 10;
          background: $orange;
        }

        .trim-start-handle,
        .trim-end-handle {
          border-radius: 5px;
          transform: translateX(-3.5px);
          width: 7px;
          height: 27.5px;
          background-color: $text-color-light;
          position: absolute;
          bottom: 0;
          top:0;
          z-index: 20;
          cursor: pointer;
          border: .5px solid rgba(255,255,255,.5);
  
          &::after {
            content: "";
            width: 100%;
            height: 1px;
            position: absolute;
            top: 40%;
            border-radius: 1rem;
          }
          &::before{
            content: "";
            width: 100%;
            height: 1px;
            position: absolute;
            top: 55%;
            border-radius: 1rem;
          }
        }
  
        .trim-start-handle::before,
        .trim-start-handle::after {
          background-color: rgb(0,128,0);
        }
  
        .trim-end-handle::before,
        .trim-end-handle::after {
          background-color: rgb(255,0,0);
        }
      }
    }
    .metadata-container{
      height: 2rem;
      background: $background-darker;
      color: $text-color-light;
      padding: 1.25rem 0;
  
      .metadata {
        background: $background-darker;
        height: 20px;
  
        .trim-details{
          width: 80px;
          min-width: max-content;
        }
      }
      .btn-green{
        background:  #0c8300;
      }
    }

    .time-input {
      width: 60px;
      padding: 2px 4px;
      background: $background-darker;
      color: $text-color-light;
      border: 1px solid $background-medium;
      border-radius: 3px;
      font-size: 12px;
      text-align: center;
      
      &:focus {
        outline: none;
        border-color: #0c8300;
      }
    }

    .btn-carma {
      background: #ff9800;
      color: white;
      
      &:hover {
        background: #f57c00;
      }
    }

    .merge-popup-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      z-index: 9999;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .merge-popup {
      background: #222;
      padding: 20px;
      border-radius: 8px;
      width: 260px;
      color: white;
      position: relative;

      h4 {
        margin: 0 0 12px 0;
        color: $text-color-light;
      }

      label {
        font-size: 12px;
        color: $text-color-light;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .available-count {
          font-size: 11px;
          color: #999;
          font-weight: normal;
        }
      }

      input {
        background: #444;
        color: #fff;
        border: 1px solid #555;
        padding: 4px;
        border-radius: 3px;
        font-size: 12px;
        width: 100%;

        &:focus {
          outline: none;
          border-color: #ff9800;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }

      .merge-preview {
        margin-top: 8px;
        padding: 8px;
        background: rgba(255, 152, 0, 0.1);
        border-radius: 3px;
        border: 1px solid rgba(255, 152, 0, 0.3);

        .preview-label {
          font-size: 11px;
          color: #999;
          margin-bottom: 4px;
        }

        .preview-time {
          font-size: 13px;
          color: #ff9800;
          font-weight: bold;
        }
      }


      .actions {
        display: flex;
        gap: 8px;
        margin-top: 8px;
        justify-content: flex-end;

        button {
          min-width: 60px;
        }
      }
    }
  </style>